import type { IWedding_Website } from '@bridebook/models/source/models/Weddings.types';
import {
  FONT_STYLES,
  FONTS_PAIRS,
  PALETTE_BACKGROUNDS,
  PALETTE_COLORS,
  PALETTE_EXTRA_ASSETS,
  PALETTE_HIGHLIGHT_COLORS,
  PALETTE_TEXT_COLORS,
  PALETTE_THUMBNAIL_COLORS,
  type TPaletteColors,
  type TWebsiteBackgroundType,
} from '@bridebook/web/lib/wedding-website/constants/themes';
import {
  FontPairs,
  type IFontStyle,
  Palettes,
  type TColorPaletteHighlights,
  type TFontsPair,
} from '@bridebook/web/lib/wedding-website/types';

/**
 * Interface representing all theme variables extracted from a wedding website theme configuration
 */
export interface IThemeProperties {
  /** The palette enum value */
  palette: Palettes;
  /** The font pair enum value */
  fontPair: FontPairs;
  /** All background image URLs for the palette */
  paletteBackgrounds: Record<TWebsiteBackgroundType, string>;
  /** Extra asset URL for the palette (if any) */
  paletteExtraAsset: string;
  /** All color values for the palette */
  paletteColors: TPaletteColors;
  /** Text color for the palette */
  paletteTextColor: TPaletteColors['text'];
  /** Thumbnail color for the palette */
  paletteThumbnailColor: TPaletteColors['thumbnail'];
  /** Highlight colors array for the palette */
  paletteHighlightColors: TColorPaletteHighlights;
  /** Font pair configuration (primary and secondary fonts) */
  fontsPair: TFontsPair;
  /** Primary font style configuration */
  primaryFontStyle: IFontStyle;
  /** Secondary font style configuration */
  secondaryFontStyle: IFontStyle;
}

/**
 * Extracts all theme-related variables from a wedding website theme configuration.
 * Returns ready-to-use CSS style values for palette backgrounds, colors, fonts, etc.
 *
 * @param theme - The theme configuration from IWedding_Website['config']['theme']
 * @returns Object containing all theme variables ready for use in CSS styles
 *
 * @example
 * ```typescript
 * const themeVariables = getThemeProperties(websiteConfig.theme);
 *
 * // Use in styles
 * const styles = {
 *   background: themeVariables.paletteBackgrounds.tileBackground,
 *   color: themeVariables.paletteColors.text,
 *   fontFamily: themeVariables.primaryFontStyle.fontFamily,
 *   button: {
 *     backgroundColor: themeVariables.paletteColors.button,
 *     color: themeVariables.paletteColors.buttonText,
 *   }
 * };
 * ```
 */
export const getThemeProperties = (
  theme: IWedding_Website['config']['theme'],
): IThemeProperties => {
  // Parse the theme strings to enum values
  const palette = theme.palette as Palettes;
  const fontPair = theme.font as FontPairs;

  // Get font pair configuration
  const fontsPair = FONTS_PAIRS[fontPair];

  return {
    palette,
    fontPair,

    // Palette-related assets and colors
    paletteBackgrounds: PALETTE_BACKGROUNDS[palette],
    paletteExtraAsset: PALETTE_EXTRA_ASSETS[palette],
    paletteColors: PALETTE_COLORS[palette],
    paletteTextColor: PALETTE_TEXT_COLORS[palette],
    paletteThumbnailColor: PALETTE_THUMBNAIL_COLORS[palette],
    paletteHighlightColors: PALETTE_HIGHLIGHT_COLORS[palette],

    // Font-related configurations
    fontsPair,
    primaryFontStyle: FONT_STYLES[fontsPair.primary],
    secondaryFontStyle: FONT_STYLES[fontsPair.secondary],
  };
};
